import React, { useState } from 'react';
import cn from '@/utils/cn';
import { ExitIcon, HambugerMenuIcon } from '@/components/Icons';
import AvatarIcon from '@/components/AvatarIcon';
import UserSettingsDropdownMenu from '../UserSettingsDropdownMenu';
import useAuth from '@/hooks/useAuth';
import useXero from '@/hooks/useXero';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/utils/routes';
import { useIntercom } from '../../hooks/useIntercom';
import { UserIcon } from './UserIcon';
import { AccountMenu } from './AccountMenu';
import { SettingsMenu } from './SettingsMenu';
import { AccountTypeEnum } from '@/context/types/Account';
import { TWalletData } from '@/features/home/types/WalletData';
import { TSelectOptions } from '@/components/Select';
import EmergeLogo from '@/components/logos/EmergeLogo';
import { MoveMoneyMenu } from './MoveMoneyMenu';
import { PaymentsMenu } from './PaymentsMenu';
import Ellipse from '@/components/Icons/Ellipse';
import { useProfile } from '@/hooks/useProfile';

const NavBarClassName = cn([
  'top-0',
  'left-0',
  'w-full',
  'bg-dark-navy-30',
  'h-14',
  'flex',
  'items-center',
  'text-white',
  'px-5',
  'relative'
]);

// Left cluster: logo + divider + account branding — grouped as one flex-none unit
const LeftSideClassName = cn(['flex', 'flex-none', 'items-center', 'gap-2']);

// Takes remaining space; space-between pushes nav items left and actions right
const RightSideClassName = cn(['flex', 'flex-1', 'items-center', 'justify-between', 'pl-4']);

const AnchorClassName = cn(['cursor-pointer']);

interface NavBarProps {
  onOpenXeroModal: () => void;
  onToggleNavDrawer: () => void;
  openNavDrawer: boolean;
  allWallets: TWalletData[];
  allOptions: TSelectOptions[];
}

const VerticalLine = () => <div className="h-8 w-px bg-white opacity-30 flex-none" />;

const NavBar: React.FC<NavBarProps> = ({
  onOpenXeroModal,
  onToggleNavDrawer,
  openNavDrawer,
  allWallets,
  allOptions
}) => {
  const navigate = useNavigate();
  const { business, customer } = useAuth()!;
  const { accountType, photoURL, name } = useProfile();
  const { xeroTenant } = useXero()!;
  const [showNavBarMenu, setShowNavBarMenu] = useState<string | undefined>(undefined);
  const { handleShowIntercom } = useIntercom();

  // TODO: Accounts drop down
  const handleNavBarMenuDropdown = (menu: string) => {
    setShowNavBarMenu((prev) => {
      if (prev === menu) {
        return undefined;
      } else {
        return menu;
      }
    });
  };

  return (
    <>
      <div className={NavBarClassName}>
        {/* Single flex row wrapping all navbar content */}
        <div className="flex flex-1 items-center gap-4">
          {/* Logo + divider + account branding */}
          <div className={LeftSideClassName}>
            <div style={{ width: '25.001px', height: '31.921px', aspectRatio: '25.00/31.92' }}>
              <EmergeLogo showName={false} />
            </div>
            <VerticalLine />
            <div className="flex items-center gap-2 pl-2">
              <UserIcon business={business} customer={customer!} />
            </div>
          </div>

          {/* Nav items (left) + actions (right) */}
          <div className={RightSideClassName}>
            {/* Nav items — hidden on tablet/mobile */}
            <div className="tablet:hidden flex items-center gap-6">
              {accountType === AccountTypeEnum.BUSINESS ? (
                <SettingsMenu
                  showNavBarMenu={showNavBarMenu}
                  onOpenNavigation={() => handleNavBarMenuDropdown('settings')}
                  onOpenGeneralLedgerPage={() => navigate(routes.generalLedgerSettings)}
                  onOpenTeamMembers={() => navigate(routes.teamMembers)}
                  onOpenCards={() => navigate(routes.cards)}
                  onCloseMenu={() => setShowNavBarMenu(undefined)}
                  onOpenXeroModal={onOpenXeroModal}
                  xeroTenant={xeroTenant}
                />
              ) : null}
              <AccountMenu
                showNavBarMenu={showNavBarMenu}
                onOpenNavigation={() => handleNavBarMenuDropdown('accounts')}
                onCloseMenu={() => setShowNavBarMenu(undefined)}
                allWallets={allWallets}
                allOptions={allOptions}
              />

              <PaymentsMenu />
            </div>

            {/* Right-side actions */}
            <div className="flex gap-x-2 items-center ml-auto">
              <div className="flex gap-x-4 items-center">
                <MoveMoneyMenu
                  showNavBarMenu={showNavBarMenu}
                  onOpenNavigation={() => handleNavBarMenuDropdown('movemoney')}
                  onCloseMenu={() => setShowNavBarMenu(undefined)}
                />

                <a
                  className={cn(AnchorClassName, 'flex items-center justify-center tablet:hidden')}
                  onClick={handleShowIntercom}>
                  <div className="relative flex items-center justify-center">
                    <Ellipse />
                    <span className="absolute text-emerge-navy font-light text-[20px]">?</span>
                  </div>
                </a>
                {/* Avatar + dropdown scoped together so top/right offsets are relative to the avatar */}
                <div className="relative tablet:hidden">
                  <a
                    className={AnchorClassName}
                    onClick={() => handleNavBarMenuDropdown('usersettings')}>
                    <AvatarIcon key="customer-name" name={name} imgSrc={photoURL} />
                  </a>
                  <UserSettingsDropdownMenu
                    showNavBarMenu={showNavBarMenu}
                    onCloseMenu={() => setShowNavBarMenu(undefined)}
                  />
                </div>

                <a
                  className={cn(AnchorClassName, 'hidden tablet:inline mobile:inline')}
                  onClick={onToggleNavDrawer}>
                  {openNavDrawer ? (
                    <ExitIcon className="text-emerge-teal" size="md" />
                  ) : (
                    <HambugerMenuIcon className="text-emerge-teal" size="lg" />
                  )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavBar;
